/**
 * Scenario Generator for Journey Tab
 *
 * Generates 3 return-to-India scenarios:
 * - Conservative: Delay return to maximize savings
 * - Balanced: Current plan (user's selected date)
 * - Aggressive: Return sooner
 *
 * All calculations are deterministic.
 */

import { quickRNOREstimate } from './rnorCalculator'
import { calculateTaxSavingsBreakdown, type TaxSavingsInput } from './taxSavingsCalculator'
import { calculateSSEligibility } from './socialSecurityCalculator'
import { getCountryConfig } from '@/config/countries/registry'
import type { RSUGrant, StateTaxTier } from '@/types'

// ==========================================
// Types
// ==========================================

export interface ScenarioMetrics {
  rnorYears: number
  rnorEligible: boolean
  taxSavingsINR: number
  taxSavingsFormatted: string
  additionalCorpusINR: number
  additionalCorpusFormatted: string
  ssEligibleAfter: boolean
  ssYearsNeeded: number
  riskLevel: 'low' | 'medium' | 'high'
  riskExplanation: string
}

export interface Scenario {
  id: string
  name: string
  type: 'conservative' | 'balanced' | 'aggressive'
  returnYear: number
  returnMonth: number
  returnDate: string  // Formatted for display
  returnFY: string    // e.g., "FY 2025-26"
  isRecommended: boolean
  isCurrentPlan: boolean
  metrics: ScenarioMetrics
  pros: string[]
  cons: string[]
  summary: string
}

export interface ScenarioComparison {
  scenarios: Scenario[]
  recommendedScenarioId: string
  recommendationRationale: string
  keyTradeoff: string
}

export interface ScenarioInput {
  // Profile
  nriSinceYear: number
  expectedReturnYear: number
  expectedReturnMonth?: number  // 1-12, default April (4)
  currentAge: number
  residentCountry: 'USA' | 'UAE'
  exchangeRate: number

  // For tax calculations
  totalForeignAssets: number
  stateTaxTier?: StateTaxTier
  rsuGrants?: RSUGrant[]
  esppBalance?: number
  balance401k?: number
  balanceTraditionalIRA?: number
  taxableBrokerage?: number
  brokerageCostBasis?: number
  cryptoBalance?: number  // Crypto for tax arbitrage calculation

  // For SS calculations (USA only)
  yearsWorkedInUSA?: number
  annualIncomeUSD?: number

  // For corpus calculation
  monthlySavingsUSD?: number

  // User's safe withdrawal rate (from assumptions)
  safeWithdrawalRate?: number
}

// ==========================================
// Main Generator
// ==========================================

export function generateScenarios(input: ScenarioInput): ScenarioComparison {
  const baseYear = input.expectedReturnYear
  const baseMonth = input.expectedReturnMonth || 4 // Default to April (FY start)
  const currentYear = new Date().getFullYear()

  const scenarios: Scenario[] = []

  // Scenario 1: Balanced (Current Plan)
  const balancedScenario = createScenario(input, baseYear, baseMonth, 'balanced', true)
  scenarios.push(balancedScenario)

  // Scenario 2: Conservative (Delay by 1 year)
  if (baseYear >= currentYear) {
    const conservativeScenario = createScenario(input, baseYear + 1, baseMonth, 'conservative', false)
    scenarios.push(conservativeScenario)
  }

  // Scenario 3: Aggressive (6 months earlier, if possible)
  if (baseYear > currentYear || (baseYear === currentYear && baseMonth > 6)) {
    const aggressiveYear = baseMonth <= 6 ? baseYear - 1 : baseYear
    const aggressiveMonth = baseMonth <= 6 ? baseMonth + 6 : baseMonth - 6
    const aggressiveScenario = createScenario(input, aggressiveYear, aggressiveMonth, 'aggressive', false)
    scenarios.push(aggressiveScenario)
  }

  // Determine recommended scenario
  const recommended = determineRecommendedScenario(scenarios, input)
  recommended.isRecommended = true

  // Generate rationale
  const { rationale, keyTradeoff } = generateRationale(scenarios, recommended, input)

  return {
    scenarios,
    recommendedScenarioId: recommended.id,
    recommendationRationale: rationale,
    keyTradeoff
  }
}

// ==========================================
// Scenario Creation
// ==========================================

function createScenario(
  input: ScenarioInput,
  returnYear: number,
  returnMonth: number,
  type: Scenario['type'],
  isCurrentPlan: boolean
): Scenario {
  const id = `scenario-${type}`
  const name = getScenarioName(type)

  // Calculate RNOR eligibility for this return date
  const rnorResult = quickRNOREstimate(input.nriSinceYear, returnYear)

  // Calculate tax savings for this scenario
  const taxInput: TaxSavingsInput = {
    residentCountry: input.residentCountry,
    exchangeRate: input.exchangeRate,
    stateTaxTier: input.stateTaxTier,
    rsuGrants: input.rsuGrants,
    esppBalance: input.esppBalance,
    balance401k: input.balance401k,
    balanceTraditionalIRA: input.balanceTraditionalIRA,
    taxableBrokerage: input.taxableBrokerage,
    brokerageCostBasis: input.brokerageCostBasis,
    cryptoBalance: input.cryptoBalance,  // Crypto for tax arbitrage
    rnorEligible: rnorResult.eligible,
    rnorYears: rnorResult.estimatedYears,
    totalForeignAssets: input.totalForeignAssets,
    safeWithdrawalRate: input.safeWithdrawalRate
  }
  const taxSavings = calculateTaxSavingsBreakdown(taxInput)

  // Calculate SS eligibility (countries with Social Security system)
  const countryConfig = getCountryConfig(input.residentCountry)
  let ssEligibleAfter = false
  let ssYearsNeeded = 0
  if (countryConfig.benefits.hasSocialSecurity && input.yearsWorkedInUSA !== undefined) {
    const yearsUntilReturn = returnYear - new Date().getFullYear()
    const projectedYearsWorked = input.yearsWorkedInUSA + Math.max(0, yearsUntilReturn)
    const ssResult = calculateSSEligibility(
      projectedYearsWorked,
      input.currentAge + yearsUntilReturn,
      input.annualIncomeUSD || 150000,
      input.exchangeRate
    )
    if (ssResult) {
      ssEligibleAfter = ssResult.eligible
      ssYearsNeeded = ssResult.yearsToQualify
    }
  }

  // Calculate additional corpus from delayed return
  const yearsFromBase = returnYear - input.expectedReturnYear
  const additionalCorpusUSD = (input.monthlySavingsUSD || 5000) * 12 * yearsFromBase
  const additionalCorpusINR = additionalCorpusUSD * input.exchangeRate

  // Calculate risk level
  const { riskLevel, riskExplanation } = calculateRiskLevel(type, rnorResult.eligible)

  const metrics: ScenarioMetrics = {
    rnorYears: rnorResult.estimatedYears,
    rnorEligible: rnorResult.eligible,
    taxSavingsINR: taxSavings.total,
    taxSavingsFormatted: taxSavings.totalFormatted,
    additionalCorpusINR: Math.round(additionalCorpusINR),
    additionalCorpusFormatted: formatINR(Math.round(additionalCorpusINR)),
    ssEligibleAfter,
    ssYearsNeeded,
    riskLevel,
    riskExplanation
  }

  // Generate pros and cons
  const { pros, cons } = generateProsAndCons(type, metrics, yearsFromBase, input)

  // Generate summary
  const summary = generateSummary(type, metrics, returnYear)

  return {
    id,
    name,
    type,
    returnYear,
    returnMonth,
    returnDate: formatReturnDate(returnYear, returnMonth),
    returnFY: getFY(returnYear, returnMonth),
    isRecommended: false,
    isCurrentPlan,
    metrics,
    pros,
    cons,
    summary
  }
}

// ==========================================
// Helpers
// ==========================================

function getScenarioName(type: Scenario['type']): string {
  switch (type) {
    case 'conservative': return 'Delay 1 Year'
    case 'balanced': return 'Current Plan'
    case 'aggressive': return 'Return Sooner'
  }
}

function formatReturnDate(year: number, month: number): string {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${monthNames[month - 1]} ${year}`
}

function getFY(year: number, month: number): string {
  // Indian FY runs Apr-Mar
  if (month >= 4) {
    return `FY ${year}-${(year + 1).toString().slice(-2)}`
  } else {
    return `FY ${year - 1}-${year.toString().slice(-2)}`
  }
}

function formatINR(amount: number): string {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`
  if (amount < 0) return `-₹${Math.abs(amount).toLocaleString('en-IN')}`
  return `₹${amount.toLocaleString('en-IN')}`
}

function calculateRiskLevel(
  type: Scenario['type'],
  rnorEligible: boolean
): { riskLevel: 'low' | 'medium' | 'high'; riskExplanation: string } {
  // Conservative: Low risk - more time to prepare
  if (type === 'conservative') {
    return {
      riskLevel: 'low',
      riskExplanation: 'Extended timeline provides flexibility for planning and market conditions'
    }
  }

  // Aggressive: Higher risk - less time, possible RNOR loss
  if (type === 'aggressive') {
    if (!rnorEligible) {
      return {
        riskLevel: 'high',
        riskExplanation: 'Earlier return may forfeit RNOR benefits, reducing tax savings potential'
      }
    }
    return {
      riskLevel: 'medium',
      riskExplanation: 'Shorter preparation window; ensure all liquidations are complete before move'
    }
  }

  // Balanced: Risk depends on RNOR status
  if (!rnorEligible) {
    return {
      riskLevel: 'medium',
      riskExplanation: 'RNOR not eligible; foreign income will be taxable from year one'
    }
  }

  return {
    riskLevel: 'low',
    riskExplanation: 'Balanced approach with RNOR benefits and reasonable preparation time'
  }
}

function generateProsAndCons(
  type: Scenario['type'],
  metrics: ScenarioMetrics,
  _yearsFromBase: number,
  input: ScenarioInput
): { pros: string[]; cons: string[] } {
  const pros: string[] = []
  const cons: string[] = []

  switch (type) {
    case 'conservative':
      if (metrics.additionalCorpusINR > 0) {
        pros.push(`Build ${metrics.additionalCorpusFormatted} more corpus`)
      }
      if (metrics.rnorYears > 0) {
        pros.push(`${metrics.rnorYears}-year RNOR window`)
      }
      const ssConfig = getCountryConfig(input.residentCountry)
      if (!metrics.ssEligibleAfter && metrics.ssYearsNeeded <= 2 && ssConfig.benefits.hasSocialSecurity) {
        pros.push('May qualify for Social Security')
      }
      cons.push('1 more year abroad')
      cons.push('Family/personal goals delayed')
      break

    case 'balanced':
      pros.push('Your chosen timeline')
      if (metrics.rnorEligible) {
        pros.push(`${metrics.rnorYears}-year RNOR benefit`)
      }
      pros.push(`${metrics.taxSavingsFormatted} estimated tax savings`)
      if (!metrics.rnorEligible) {
        cons.push('RNOR not eligible')
      }
      break

    case 'aggressive':
      pros.push('Return home sooner')
      pros.push('Start India life earlier')
      if (metrics.rnorEligible) {
        pros.push(`Still eligible for ${metrics.rnorYears}-year RNOR`)
      }
      if (metrics.additionalCorpusINR < 0) {
        cons.push(`${formatINR(Math.abs(metrics.additionalCorpusINR))} less corpus`)
      }
      if (!metrics.rnorEligible) {
        cons.push('May lose RNOR eligibility')
      }
      break
  }

  // Limit to 3 each
  return {
    pros: pros.slice(0, 3),
    cons: cons.slice(0, 3)
  }
}

function generateSummary(type: Scenario['type'], metrics: ScenarioMetrics, returnYear: number): string {
  switch (type) {
    case 'conservative':
      return `Return in ${returnYear} with ${metrics.taxSavingsFormatted} tax savings and ${metrics.additionalCorpusFormatted} additional corpus.`
    case 'balanced':
      return `Your planned return with ${metrics.taxSavingsFormatted} in tax optimization opportunities.`
    case 'aggressive':
      return `Return sooner in ${returnYear}. ${metrics.rnorEligible ? `RNOR still applies for ${metrics.rnorYears} years.` : 'RNOR may not apply.'}`
  }
}

function determineRecommendedScenario(scenarios: Scenario[], input: ScenarioInput): Scenario {
  // Simple heuristic: Recommend balanced unless:
  // 1. Conservative has significantly more tax savings AND SS eligibility
  // 2. User is close to SS eligibility (within 2 years)

  const balanced = scenarios.find(s => s.type === 'balanced')
  const conservative = scenarios.find(s => s.type === 'conservative')

  // Safety check: if no balanced scenario exists, return first available
  if (!balanced) {
    if (scenarios.length === 0) {
      throw new Error('No scenarios available to recommend')
    }
    // TypeScript can't narrow array[0] after length check, so assert
    return scenarios[0] as Scenario
  }

  // Check if delaying 1 year unlocks SS (for countries with Social Security)
  const recommendConfig = getCountryConfig(input.residentCountry)
  if (conservative && recommendConfig.benefits.hasSocialSecurity) {
    const currentSSResult = calculateSSEligibility(
      input.yearsWorkedInUSA || 0,
      input.currentAge,
      input.annualIncomeUSD || 150000,
      input.exchangeRate
    )

    if (currentSSResult && !currentSSResult.eligible && currentSSResult.yearsToQualify <= 1) {
      // Delaying 1 year unlocks SS - strongly recommend conservative
      return conservative
    }

    // If conservative has 20%+ more tax savings, consider recommending it
    if (conservative.metrics.taxSavingsINR > balanced.metrics.taxSavingsINR * 1.2) {
      return conservative
    }
  }

  return balanced
}

function generateRationale(
  scenarios: Scenario[],
  recommended: Scenario,
  input: ScenarioInput
): { rationale: string; keyTradeoff: string } {
  const balanced = scenarios.find(s => s.type === 'balanced')!
  const conservative = scenarios.find(s => s.type === 'conservative')

  let rationale = ''
  let keyTradeoff = ''

  const rationaleConfig = getCountryConfig(input.residentCountry)
  if (recommended.type === 'conservative') {
    rationale = `Delaying your return by 1 year provides ${recommended.metrics.taxSavingsFormatted} in tax savings and builds ${recommended.metrics.additionalCorpusFormatted} additional corpus.`
    if (rationaleConfig.benefits.hasSocialSecurity && recommended.metrics.ssEligibleAfter && !balanced.metrics.ssEligibleAfter) {
      rationale += ' Most importantly, this timing may qualify you for Social Security benefits.'
    }
    keyTradeoff = '1 year longer abroad vs significant financial benefit'
  } else if (recommended.type === 'balanced') {
    rationale = `Your planned timeline of ${balanced.returnDate} optimizes for both financial benefits (${balanced.metrics.taxSavingsFormatted} savings) and personal goals.`
    if (conservative && conservative.metrics.taxSavingsINR > balanced.metrics.taxSavingsINR) {
      keyTradeoff = `Delaying 1 year adds ${conservative.metrics.additionalCorpusFormatted} corpus but extends time abroad`
    } else {
      keyTradeoff = 'Current plan balances finances and lifestyle'
    }
  } else {
    rationale = `Returning sooner gets you home faster while still preserving ${recommended.metrics.rnorYears} years of RNOR benefits.`
    keyTradeoff = 'Earlier return vs less corpus accumulation'
  }

  return { rationale, keyTradeoff }
}
